import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { toast } from 'sonner';
import { hasCapability } from '@/lib/capabilities';
import { paiseToRupees, rupeesToPaise } from '@/lib/money';

// UI edits money in rupees; catalog persists in paise. Convert at editor open/save.
const toEditableProduct = (p) => ({
  ...p,
  variants: (p.variants || []).map((v) => ({ ...v, price: paiseToRupees(v.price) })),
  channels: Object.fromEntries(Object.entries(p.channels || {}).map(([k, c]) => [k, { ...c, price_override: c.price_override == null ? null : paiseToRupees(c.price_override) }])),
});
const fromEditableProduct = (p) => ({
  ...p,
  variants: (p.variants || []).map((v) => ({ ...v, price: rupeesToPaise(v.price) })),
  channels: Object.fromEntries(Object.entries(p.channels || {}).map(([k, c]) => [k, { ...c, price_override: c.price_override == null || c.price_override === '' ? null : rupeesToPaise(c.price_override) }])),
});
import { Plus, Trash2, Pencil, ChevronLeft, Layers, Tag as TagIcon, Sliders, Upload, X, GripVertical, Sparkles } from 'lucide-react';

const CHANNELS = ['online', 'pos_walkin', 'dine_in', 'takeaway', 'delivery', 'aggregator'];
const CHANNEL_LABEL = { online: 'Online', pos_walkin: 'POS Walk-in', dine_in: 'Dine-In', takeaway: 'Takeaway', delivery: 'Delivery', aggregator: 'Aggregator' };
const TAG_PRESETS = ['New', 'Bestseller', 'Special Offer', 'Limited Time', 'Recommended'];

const emptyProduct = (categoryId) => ({
  category_id: categoryId || '', name: '', description: '', image_url: '', is_veg: true,
  tags: [], variants: [{ id: null, name: '', price: 0, is_default: true }],
  modifier_group_ids: [], is_combo: false, combo_components: [], display_order: 0, active: true, channels: {},
});

export default function MenuManager() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const storeId = user?.store_id;
  const canManage = hasCapability(user, 'menu.manage');

  useEffect(() => {
    if (!user?.id) { navigate('/login'); return; }
    if (!canManage) {
      toast.error('You do not have permission to manage the menu');
      navigate('/pos-dashboard');
    }
  }, []); // eslint-disable-line

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [modGroups, setModGroups] = useState([]);
  const [collections, setCollections] = useState([]);
  const [selectedCat, setSelectedCat] = useState(null);
  const [loading, setLoading] = useState(true);

  const [editProduct, setEditProduct] = useState(null);
  const [showModLibrary, setShowModLibrary] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  const [showCollections, setShowCollections] = useState(false);
  const [editCollection, setEditCollection] = useState(null);

  const load = useCallback(async () => {
    if (!storeId) return;
    try {
      const [c, p, m, col] = await Promise.all([
        apiClient.get(`/catalog/${storeId}/categories`),
        apiClient.get(`/catalog/${storeId}/products`),
        apiClient.get(`/catalog/${storeId}/modifier-groups`),
        apiClient.get(`/catalog/${storeId}/collections`),
      ]);
      setCategories(c.data.categories);
      setProducts(p.data.products);
      setModGroups(m.data.modifier_groups);
      setCollections(col.data.collections);
      if (!selectedCat && c.data.categories.length) setSelectedCat(c.data.categories[0].id);
    } catch (e) {
      toast.error('Failed to load menu');
    } finally { setLoading(false); }
  }, [storeId]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  if (!storeId) return <div className="p-8 text-center">No store assigned to this account.</div>;
  if (!canManage) return <div className="p-8 text-center">Access denied.</div>;

  const catProducts = products.filter((p) => p.category_id === selectedCat);

  const addCategory = async () => {
    const name = window.prompt('New category name (e.g. Pizza, Beverages)');
    if (!name) return;
    try {
      const res = await apiClient.post(`/catalog/${storeId}/categories`, { name, display_order: categories.length });
      setCategories((c) => [...c, res.data]);
      setSelectedCat(res.data.id);
      toast.success(`Category "${name}" added`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await apiClient.delete(`/catalog/${storeId}/categories/${id}`);
      setCategories((c) => c.filter((x) => x.id !== id));
      if (selectedCat === id) setSelectedCat(null);
      toast.success('Category deleted');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const saveProduct = async (prod) => {
    try {
      const clean = fromEditableProduct({ ...prod, variants: prod.variants.filter((v) => v.name || v.price) });
      if (prod.id) await apiClient.put(`/catalog/${storeId}/products/${prod.id}`, clean);
      else await apiClient.post(`/catalog/${storeId}/products`, clean);
      toast.success('Product saved');
      setEditProduct(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await apiClient.delete(`/catalog/${storeId}/products/${id}`);
      toast.success('Product deleted');
      load();
    } catch (e) { toast.error('Failed'); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="menu-manager">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="mm-back"><ChevronLeft className="w-4 h-4" /></Button>
          <div>
            <h1 className="font-heading text-lg font-bold text-slate-900">Menu Management</h1>
            <p className="text-xs text-slate-500">{user?.store_name || 'Your store'} · single source of truth</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowCollections(true)} data-testid="open-collections">
            <Sparkles className="w-4 h-4 mr-1" /> Collections
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowModLibrary(true)} data-testid="open-mod-library">
            <Sliders className="w-4 h-4 mr-1" /> Modifier Library
          </Button>
          <Button size="sm" className="bg-sspice hover:bg-sspice-hover" onClick={() => setEditProduct(emptyProduct(selectedCat))} data-testid="add-product-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Product
          </Button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row max-w-6xl mx-auto">
        {/* Categories rail */}
        <aside className="md:w-56 flex-shrink-0 p-3 md:border-r border-slate-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wide text-slate-500 flex items-center gap-1"><Layers className="w-3.5 h-3.5" /> Categories</span>
            <button onClick={addCategory} data-testid="add-category-btn" className="text-sspice hover:bg-red-50 rounded p-1"><Plus className="w-4 h-4" /></button>
          </div>
          <div className="flex md:flex-col gap-1 overflow-x-auto">
            {categories.map((cat) => (
              <button
                key={cat.id} data-testid={`cat-${cat.id}`}
                onClick={() => setSelectedCat(cat.id)}
                className={`group flex items-center justify-between whitespace-nowrap px-3 py-2 rounded-lg text-sm text-left transition-colors ${selectedCat === cat.id ? 'bg-sspice text-white' : 'hover:bg-slate-100 text-slate-700'}`}
              >
                <span className="font-medium">{cat.name}</span>
                <span className="flex items-center gap-1">
                  <span className={`text-xs ${selectedCat === cat.id ? 'text-white/70' : 'text-slate-400'}`}>{products.filter((p) => p.category_id === cat.id).length}</span>
                  <Trash2 onClick={(e) => { e.stopPropagation(); deleteCategory(cat.id); }} className={`w-3.5 h-3.5 opacity-0 group-hover:opacity-100 ${selectedCat === cat.id ? 'text-white' : 'text-slate-400'}`} />
                </span>
              </button>
            ))}
            {categories.length === 0 && <p className="text-xs text-slate-400 px-2">Add your first category →</p>}
          </div>
        </aside>

        {/* Products */}
        <main className="flex-1 p-4">
          {loading ? (
            <div className="text-center py-16 text-slate-400">Loading…</div>
          ) : !selectedCat ? (
            <div className="text-center py-16 text-slate-400">Select or create a category to add products.</div>
          ) : catProducts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-slate-400 mb-3">No products in this category yet.</p>
              <Button className="bg-sspice hover:bg-sspice-hover" onClick={() => setEditProduct(emptyProduct(selectedCat))}><Plus className="w-4 h-4 mr-1" /> Add Product</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {catProducts.map((p) => (
                <div key={p.id} data-testid={`product-${p.id}`} className="bg-white border border-slate-200 rounded-xl p-3 flex gap-3">
                  <div className="w-16 h-16 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0">
                    {p.image_url ? <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-300"><TagIcon className="w-5 h-5" /></div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="min-w-0">
                        <p className="font-semibold text-sm text-slate-900 truncate">{p.name}</p>
                        <p className="text-xs text-slate-500">{p.variants.map((v) => `${v.name || 'Base'} ₹${paiseToRupees(v.price)}`).join(' · ')}</p>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => setEditProduct(toEditableProduct({ ...p }))} data-testid={`edit-product-${p.id}`} className="p-1.5 text-slate-400 hover:text-sspice"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => deleteProduct(p.id)} className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {p.tags?.map((t) => <Badge key={t} className="bg-sspice text-white text-[10px]">{t}</Badge>)}
                      {p.modifier_group_ids?.length > 0 && <Badge variant="outline" className="text-[10px]">{p.modifier_group_ids.length} modifier group(s)</Badge>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      {editProduct && (
        <ProductEditor
          product={editProduct} categories={categories} modGroups={modGroups}
          storeId={storeId} onClose={() => setEditProduct(null)} onSave={saveProduct}
          onManageModifiers={() => setShowModLibrary(true)}
        />
      )}

      <ModifierLibrary
        open={showModLibrary} onOpenChange={setShowModLibrary} storeId={storeId}
        groups={modGroups} editGroup={editGroup} setEditGroup={setEditGroup} onChanged={load}
      />

      <CollectionsManager
        open={showCollections} onOpenChange={setShowCollections} storeId={storeId}
        collections={collections} products={products} editCollection={editCollection}
        setEditCollection={setEditCollection} onChanged={load}
      />
    </div>
  );
}

// ---------------- Product Editor (unified: details + variants + modifiers + channels) ----------------
function ProductEditor({ product, categories, modGroups, storeId, onClose, onSave, onManageModifiers }) {
  const [p, setP] = useState(product);
  const [uploading, setUploading] = useState(false);
  const up = (patch) => setP((x) => ({ ...x, ...patch }));

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const presignRes = await apiClient.post(
        `/menu/upload-presigned-url?filename=${encodeURIComponent(file.name)}&store_id=${storeId}`
      );
      const { upload_url, public_url } = presignRes.data;
      const putRes = await fetch(upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      if (!putRes.ok) throw new Error('S3 upload failed');
      up({ image_url: public_url });
      toast.success('Image uploaded');
    } catch { toast.error('Upload failed'); } finally { setUploading(false); e.target.value = ''; }
  };

  const setVariant = (i, patch) => up({ variants: p.variants.map((v, idx) => idx === i ? { ...v, ...patch } : v) });
  const setDefault = (i) => up({ variants: p.variants.map((v, idx) => ({ ...v, is_default: idx === i })) });
  const channelCfg = (ch) => p.channels?.[ch] || { available: true, visible: true, price_override: null };
  const setChannel = (ch, patch) => up({ channels: { ...p.channels, [ch]: { ...channelCfg(ch), ...patch } } });
  const toggleTag = (t) => up({ tags: p.tags.includes(t) ? p.tags.filter((x) => x !== t) : [...p.tags, t] });
  const toggleGroup = (id) => up({ modifier_group_ids: p.modifier_group_ids.includes(id) ? p.modifier_group_ids.filter((x) => x !== id) : [...p.modifier_group_ids, id] });

  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="product-editor">
        <SheetHeader><SheetTitle>{p.id ? 'Edit' : 'New'} Product</SheetTitle></SheetHeader>
        <div className="space-y-5 py-4">
          {/* Details */}
          <div className="space-y-2">
            <Label>Name</Label>
            <Input data-testid="pe-name" value={p.name} onChange={(e) => up({ name: e.target.value })} placeholder="e.g. Margherita" />
            <Label>Description</Label>
            <Textarea value={p.description} onChange={(e) => up({ description: e.target.value })} placeholder="Short description" rows={2} />
            <div className="flex items-center gap-2 pt-1">
              <Switch checked={p.is_veg} onCheckedChange={(v) => up({ is_veg: v })} data-testid="pe-veg" />
              <Label className="cursor-pointer">Vegetarian</Label>
            </div>
          </div>

          {/* Image */}
          <div>
            <Label>Image</Label>
            <div className="flex items-center gap-3 mt-1">
              <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-2 bg-red-50 text-sspice border border-red-200 rounded-md text-sm font-medium">
                <Upload className="w-4 h-4" /> {uploading ? 'Uploading…' : 'Upload'}
                <input type="file" accept="image/*" className="hidden" onChange={uploadImage} data-testid="pe-image" />
              </label>
              {p.image_url && <img src={p.image_url} alt="" className="w-14 h-14 rounded object-cover border" />}
            </div>
          </div>

          {/* Tags */}
          <div>
            <Label>Labels</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {TAG_PRESETS.map((t) => (
                <button key={t} onClick={() => toggleTag(t)} data-testid={`pe-tag-${t}`}
                  className={`text-xs px-2.5 py-1 rounded-full border ${p.tags.includes(t) ? 'bg-sspice text-white border-sspice' : 'bg-white text-slate-600 border-slate-200'}`}>{t}</button>
              ))}
            </div>
          </div>

          {/* Variants */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Variants (e.g. Size)</Label>
              <Button size="sm" variant="outline" onClick={() => up({ variants: [...p.variants, { id: null, name: '', price: 0, is_default: p.variants.length === 0 }] })} data-testid="pe-add-variant"><Plus className="w-3 h-3 mr-1" /> Add</Button>
            </div>
            <div className="space-y-2">
              {p.variants.map((v, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`pe-variant-${i}`}>
                  <Input className="flex-1" placeholder="Size (e.g. 8 Inch) — blank = single" value={v.name} onChange={(e) => setVariant(i, { name: e.target.value })} />
                  <Input className="w-24" type="number" placeholder="Price" value={v.price} onChange={(e) => setVariant(i, { price: parseFloat(e.target.value) || 0 })} />
                  <button onClick={() => setDefault(i)} title="Default" className={`text-xs px-2 py-1 rounded ${v.is_default ? 'bg-sspice text-white' : 'bg-slate-100 text-slate-500'}`}>Default</button>
                  {p.variants.length > 1 && <button onClick={() => up({ variants: p.variants.filter((_, idx) => idx !== i) })}><X className="w-4 h-4 text-slate-400" /></button>}
                </div>
              ))}
            </div>
          </div>

          {/* Modifier groups */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Modifier Groups (Crust, Toppings…)</Label>
              <Button size="sm" variant="ghost" className="text-sspice" onClick={onManageModifiers}>Manage library</Button>
            </div>
            <div className="space-y-1.5">
              {modGroups.length === 0 && <p className="text-xs text-slate-400">No modifier groups yet — create them in the Modifier Library.</p>}
              {modGroups.map((g) => (
                <label key={g.id} className="flex items-center gap-2 p-2 border border-slate-200 rounded-lg cursor-pointer" data-testid={`pe-modgroup-${g.id}`}>
                  <input type="checkbox" checked={p.modifier_group_ids.includes(g.id)} onChange={() => toggleGroup(g.id)} />
                  <span className="text-sm font-medium">{g.name}</span>
                  <span className="text-xs text-slate-400">{g.selection_type} · {g.options.length} options{g.required ? ' · required' : ''}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Category */}
          <div>
            <Label>Category</Label>
            <select className="w-full mt-1 border border-slate-200 rounded-md px-3 py-2 text-sm" value={p.category_id} onChange={(e) => up({ category_id: e.target.value })} data-testid="pe-category">
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          {/* Commercial rules per channel */}
          <div>
            <Label>Commercial Rules (per channel)</Label>
            <div className="mt-2 space-y-1.5">
              {CHANNELS.map((ch) => {
                const c = channelCfg(ch);
                return (
                  <div key={ch} className="flex items-center gap-2 text-sm" data-testid={`pe-channel-${ch}`}>
                    <span className="w-24 text-slate-600">{CHANNEL_LABEL[ch]}</span>
                    <label className="flex items-center gap-1 text-xs"><Switch checked={c.available} onCheckedChange={(v) => setChannel(ch, { available: v })} /> Avail</label>
                    <label className="flex items-center gap-1 text-xs"><Switch checked={c.visible} onCheckedChange={(v) => setChannel(ch, { visible: v })} /> Visible</label>
                    <Input className="w-24 h-8" type="number" placeholder="Price override" value={c.price_override ?? ''} onChange={(e) => setChannel(ch, { price_override: e.target.value === '' ? null : parseFloat(e.target.value) })} />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="flex gap-2 pb-6">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button className="flex-1 bg-sspice hover:bg-sspice-hover" onClick={() => onSave(p)} data-testid="pe-save" disabled={!p.name || !p.category_id}>Save Product</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ---------------- Modifier Library (reusable groups) ----------------
function ModifierLibrary({ open, onOpenChange, storeId, groups, editGroup, setEditGroup, onChanged }) {
  const blank = { name: '', selection_type: 'single', required: false, min_select: 0, max_select: 1, options: [{ id: null, name: '', price_delta: 0, is_default: false }] };
  const g = editGroup;

  const save = async () => {
    try {
      const body = { ...g, options: g.options.filter((o) => o.name).map((o) => ({ ...o, price_delta: rupeesToPaise(o.price_delta) })) };
      if (g.id) await apiClient.put(`/catalog/${storeId}/modifier-groups/${g.id}`, body);
      else await apiClient.post(`/catalog/${storeId}/modifier-groups`, body);
      toast.success('Modifier group saved');
      setEditGroup(null); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this modifier group?')) return;
    await apiClient.delete(`/catalog/${storeId}/modifier-groups/${id}`);
    toast.success('Deleted'); onChanged();
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setEditGroup(null); }}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="modifier-library">
        <SheetHeader><SheetTitle>Modifier Library</SheetTitle></SheetHeader>
        {!g ? (
          <div className="py-4 space-y-2">
            <Button className="w-full bg-sspice hover:bg-sspice-hover" onClick={() => setEditGroup(blank)} data-testid="add-modgroup-btn"><Plus className="w-4 h-4 mr-1" /> New Modifier Group</Button>
            {groups.map((grp) => (
              <div key={grp.id} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg" data-testid={`modgroup-${grp.id}`}>
                <div>
                  <p className="font-semibold text-sm">{grp.name}</p>
                  <p className="text-xs text-slate-400">{grp.selection_type} · {grp.options.length} options{grp.required ? ' · required' : ''}</p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setEditGroup({ ...grp, options: (grp.options || []).map((o) => ({ ...o, price_delta: paiseToRupees(o.price_delta) })) })} className="p-1.5 text-slate-400 hover:text-sspice"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => del(grp.id)} className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
            {groups.length === 0 && <p className="text-xs text-slate-400 text-center py-4">Create reusable groups like Crust, Toppings, Extras.</p>}
          </div>
        ) : (
          <div className="py-4 space-y-3">
            <div><Label>Group name</Label><Input value={g.name} onChange={(e) => setEditGroup({ ...g, name: e.target.value })} placeholder="e.g. Crust" data-testid="mg-name" /></div>
            <div className="flex gap-3">
              <div className="flex-1">
                <Label>Selection</Label>
                <select className="w-full mt-1 border rounded-md px-2 py-2 text-sm" value={g.selection_type} onChange={(e) => setEditGroup({ ...g, selection_type: e.target.value, max_select: e.target.value === 'single' ? 1 : 5 })}>
                  <option value="single">Single choice</option>
                  <option value="multi">Multiple choice</option>
                </select>
              </div>
              <label className="flex items-center gap-2 pt-6"><Switch checked={g.required} onCheckedChange={(v) => setEditGroup({ ...g, required: v, min_select: v ? 1 : 0 })} /> Required</label>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1"><Label>Options</Label>
                <Button size="sm" variant="outline" onClick={() => setEditGroup({ ...g, options: [...g.options, { id: null, name: '', price_delta: 0 }] })} data-testid="mg-add-option"><Plus className="w-3 h-3" /></Button>
              </div>
              {g.options.map((o, i) => (
                <div key={i} className="flex items-center gap-2 mb-2">
                  <Input className="flex-1" placeholder="Option (e.g. Thin Crust)" value={o.name} onChange={(e) => setEditGroup({ ...g, options: g.options.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x) })} />
                  <Input className="w-20" type="number" placeholder="+₹" value={o.price_delta} onChange={(e) => setEditGroup({ ...g, options: g.options.map((x, idx) => idx === i ? { ...x, price_delta: parseFloat(e.target.value) || 0 } : x) })} />
                  <button onClick={() => setEditGroup({ ...g, options: g.options.filter((_, idx) => idx !== i) })}><X className="w-4 h-4 text-slate-400" /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setEditGroup(null)}>Back</Button>
              <Button className="flex-1 bg-sspice hover:bg-sspice-hover" onClick={save} data-testid="mg-save" disabled={!g.name}>Save Group</Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ---------------- Collections Manager (Best Sellers, BOGO, Under ₹199, ...) ----------------
function CollectionsManager({ open, onOpenChange, storeId, collections, products, editCollection, setEditCollection, onChanged }) {
  const blank = {
    name: '', subtitle: '', type: 'manual', product_ids: [], rule: { max_price: null, tag: '' },
    banner_image: '', display_order: collections.length, active: true, starts_at: null, ends_at: null,
  };
  const c = editCollection;
  const allTags = Array.from(new Set(products.flatMap((p) => p.tags || [])));

  const toLocalInput = (iso) => (iso ? iso.slice(0, 16) : '');
  const toIso = (v) => (v ? new Date(v).toISOString() : null);

  const save = async () => {
    try {
      const rule = c.type === 'rule'
        ? { ...c.rule, max_price: c.rule?.max_price == null || c.rule?.max_price === '' ? null : rupeesToPaise(c.rule.max_price) }
        : {};
      const body = { ...c, rule };
      if (c.id) await apiClient.put(`/catalog/${storeId}/collections/${c.id}`, body);
      else await apiClient.post(`/catalog/${storeId}/collections`, body);
      toast.success('Collection saved');
      setEditCollection(null); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this collection?')) return;
    await apiClient.delete(`/catalog/${storeId}/collections/${id}`);
    toast.success('Deleted'); onChanged();
  };
  const toggleProduct = (id) => setEditCollection({ ...c, product_ids: c.product_ids.includes(id) ? c.product_ids.filter((x) => x !== id) : [...c.product_ids, id] });

  return (
    <Sheet open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setEditCollection(null); }}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="collections-manager">
        <SheetHeader><SheetTitle>Collections</SheetTitle></SheetHeader>
        {!c ? (
          <div className="py-4 space-y-2">
            <p className="text-xs text-slate-500">Curated rows for the storefront (Best Sellers, New Arrivals, Under ₹199, BOGO…). They reference existing products — no duplication.</p>
            <Button className="w-full bg-sspice hover:bg-sspice-hover" onClick={() => setEditCollection(blank)} data-testid="add-collection-btn"><Plus className="w-4 h-4 mr-1" /> New Collection</Button>
            {collections.map((col) => (
              <div key={col.id} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg" data-testid={`collection-${col.id}`}>
                <div className="min-w-0">
                  <p className="font-semibold text-sm truncate">{col.name} {!col.active && <span className="text-[10px] text-slate-400">(inactive)</span>}</p>
                  <p className="text-xs text-slate-400">{col.type === 'rule' ? `Rule${col.rule?.max_price ? ` · ≤₹${paiseToRupees(col.rule.max_price)}` : ''}${col.rule?.tag ? ` · ${col.rule.tag}` : ''}` : `${col.product_ids?.length || 0} products`}</p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setEditCollection({ ...blank, ...col, rule: { ...col.rule, max_price: col.rule?.max_price == null ? null : paiseToRupees(col.rule.max_price) } })} className="p-1.5 text-slate-400 hover:text-sspice"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => del(col.id)} className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
            {collections.length === 0 && <p className="text-xs text-slate-400 text-center py-4">No collections yet.</p>}
          </div>
        ) : (
          <div className="py-4 space-y-3">
            <div><Label>Name</Label><Input value={c.name} onChange={(e) => setEditCollection({ ...c, name: e.target.value })} placeholder="e.g. Best Sellers" data-testid="col-name" /></div>
            <div><Label>Subtitle</Label><Input value={c.subtitle} onChange={(e) => setEditCollection({ ...c, subtitle: e.target.value })} placeholder="Optional tagline" /></div>
            <div>
              <Label>Type</Label>
              <select className="w-full mt-1 border rounded-md px-2 py-2 text-sm" value={c.type} onChange={(e) => setEditCollection({ ...c, type: e.target.value })} data-testid="col-type">
                <option value="manual">Manual (pick products)</option>
                <option value="rule">Automatic (rule-based)</option>
              </select>
            </div>

            {c.type === 'rule' ? (
              <div className="space-y-2 p-3 bg-slate-50 rounded-lg">
                <div><Label>Max price (₹)</Label><Input type="number" value={c.rule?.max_price ?? ''} onChange={(e) => setEditCollection({ ...c, rule: { ...c.rule, max_price: e.target.value === '' ? null : parseFloat(e.target.value) } })} placeholder="e.g. 199" data-testid="col-maxprice" /></div>
                <div>
                  <Label>Tag</Label>
                  <select className="w-full mt-1 border rounded-md px-2 py-2 text-sm" value={c.rule?.tag || ''} onChange={(e) => setEditCollection({ ...c, rule: { ...c.rule, tag: e.target.value } })}>
                    <option value="">Any tag</option>
                    {allTags.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
            ) : (
              <div>
                <Label>Products</Label>
                <div className="mt-1 max-h-56 overflow-y-auto border border-slate-200 rounded-lg divide-y">
                  {products.map((p) => (
                    <label key={p.id} className="flex items-center gap-2 p-2 cursor-pointer" data-testid={`col-product-${p.id}`}>
                      <input type="checkbox" checked={c.product_ids.includes(p.id)} onChange={() => toggleProduct(p.id)} />
                      <span className="text-sm">{p.name}</span>
                    </label>
                  ))}
                  {products.length === 0 && <p className="text-xs text-slate-400 p-3">Add products first.</p>}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Starts (optional)</Label><Input type="datetime-local" value={toLocalInput(c.starts_at)} onChange={(e) => setEditCollection({ ...c, starts_at: toIso(e.target.value) })} /></div>
              <div><Label className="text-xs">Ends (optional)</Label><Input type="datetime-local" value={toLocalInput(c.ends_at)} onChange={(e) => setEditCollection({ ...c, ends_at: toIso(e.target.value) })} /></div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2"><Switch checked={c.active} onCheckedChange={(v) => setEditCollection({ ...c, active: v })} data-testid="col-active" /><Label className="cursor-pointer">Active</Label></div>
              <div className="flex items-center gap-2"><Label>Order</Label><Input className="w-16 h-8" type="number" value={c.display_order} onChange={(e) => setEditCollection({ ...c, display_order: parseInt(e.target.value) || 0 })} /></div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setEditCollection(null)}>Back</Button>
              <Button className="flex-1 bg-sspice hover:bg-sspice-hover" onClick={save} data-testid="col-save" disabled={!c.name}>Save Collection</Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

